<template>
  <div class="error-page" :class="darkMode ? 'error-page--dark' : 'error-page--light'">
    
    <div class="error-container">
      
      <!-- ── ICON ── -->
      <div class="error-icon-wrap">
        <div class="error-icon-bg"></div>
        <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="error-icon" />
      </div>

      <!-- ── CONTENT ── -->
      <div class="error-content">
        <h1 class="error-title" :class="darkMode ? 'text-white' : 'text-gray-900'">
          Hopa! Ceva nu a mers bine
        </h1>
        <p class="error-message" :class="darkMode ? 'text-gray-400' : 'text-gray-500'">
          Ne pare rău, dar a apărut o problemă la încărcarea acestei pagini. 
          Vă rugăm încercați din nou peste câteva momente.
        </p>
      </div>

      <!-- ── ALERT (dacă serverul încă nu merge) ── -->
      <div v-if="errorAlert" class="error-alert" :class="darkMode ? 'error-alert--dark' : 'error-alert--light'">
        <font-awesome-icon :icon="['fas', 'circle-info']" />
        <p>{{ errorAlert }}</p>
      </div>

      <!-- ── ACTIONS ── -->
      <div class="error-actions">
        <button @click="handleRetry" class="btn btn-primary">
          <font-awesome-icon :icon="['fas', 'rotate']" :spin="retrying" />
          {{ retrying ? 'Se încearcă...' : 'Încearcă Din Nou' }}
        </button>
        
        <button @click="handleGoHome" class="btn btn-secondary" :class="darkMode ? 'btn-secondary--dark' : 'btn-secondary--light'">
          <font-awesome-icon :icon="['fas', goingHome ? 'spinner' : 'home']" :spin="goingHome" />
          {{ goingHome ? 'Se verifică...' : 'Înapoi Acasă' }}
        </button>
      </div>

      <!-- ── SUGGESTIONS ── -->
      <div class="error-suggestions">
        <p class="suggestions-title" :class="darkMode ? 'text-gray-400' : 'text-gray-500'">
          <font-awesome-icon :icon="['fas', 'lightbulb']" />
          Ce puteți face:
        </p>
        <ul class="suggestions-list" :class="darkMode ? 'text-gray-400' : 'text-gray-500'">
          <li>Reîmprospătați pagina (apăsați F5)</li>
          <li>Verificați conexiunea la internet</li>
          <li>Încercați din nou peste câteva minute</li>
        </ul>
      </div>

    </div>

    <!-- ── FLOATING SHAPES ── -->
    <div class="floating-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
    </div>

  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/stores/useAppStore';
import axios from 'axios';

const app = useAppStore();
const router = useRouter();
const darkMode = computed(() => app.darkMode);

const retrying = ref(false);
const goingHome = ref(false);
const errorAlert = ref(null);

// Salvăm titlul original
const originalTitle = ref('');

onMounted(() => {
  // Salvăm titlul original
  originalTitle.value = document.title;
  // Setăm titlul pentru pagina de eroare
  document.title = 'Eroare - ' + (app.schoolName || 'Platformă Educațională');
});

onUnmounted(() => {
  // Restaurăm titlul original când părăsim pagina
  document.title = originalTitle.value;
});

const handleRetry = async () => {
  if (retrying.value) return;
  
  retrying.value = true;
  
  // Așteptăm puțin pentru UX
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Reîncarcă pagina
  window.location.reload();
};

const handleGoHome = async () => {
  if (goingHome.value) return;
  
  goingHome.value = true;
  errorAlert.value = null; // Resetăm alertul anterior
  
  try {
    // ✅ Verificăm DIRECT dacă API-ul răspunde (fără să folosim store-ul care redirectează)
    await axios.get('/school/details/');
    
    // Dacă a mers request-ul, navigăm acasă
    router.push('/');
  } catch (error) {
    // Dacă tot nu merge, afișăm un mesaj elegant
    console.error('❌ Serverul încă nu răspunde');
    errorAlert.value = 'Serverul încă nu este disponibil. Vă rugăm așteptați câteva momente și încercați din nou.';
  } finally {
    goingHome.value = false;
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@400;500;600;700&display=swap');

.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'DM Sans', sans-serif;
  position: relative;
  overflow: hidden;
}

.error-page--light {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.error-page--dark {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

/* ── CONTAINER ── */
.error-container {
  position: relative;
  z-index: 10;
  max-width: 600px;
  width: 100%;
  text-align: center;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── ICON ── */
.error-icon-wrap {
  position: relative;
  display: inline-flex;
  margin-bottom: 32px;
}

.error-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--primary-color)15, transparent);
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { 
    transform: translate(-50%, -50%) scale(1); 
    opacity: 0.3; 
  }
  50% { 
    transform: translate(-50%, -50%) scale(1.1); 
    opacity: 0.5; 
  }
}

.error-icon {
  font-size: 5rem;
  color: var(--primary-color);
  filter: drop-shadow(0 10px 25px rgba(0, 0, 0, 0.15));
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

/* ── CONTENT ── */
.error-content {
  margin-bottom: 40px;
}

.error-title {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 16px;
  letter-spacing: -0.02em;
}

.error-message {
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
}

/* ── ALERT ── */
.error-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid;
  margin: 24px auto 20px; /* 👈 Adăugat margin-bottom */
  max-width: 500px;
  font-size: 0.9rem;
  line-height: 1.5;
  animation: slideDown 0.3s ease-out;
}

.error-alert--light {
  background: #fef3c7;
  border-color: #fcd34d;
  color: #92400e;
}

.error-alert--dark {
  background: #451a03;
  border-color: #92400e;
  color: #fcd34d;
}

.error-alert svg {
  flex-shrink: 0;
  margin-top: 2px;
  opacity: 0.8;
}

.error-alert p {
  margin: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── ACTIONS ── */
.error-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 32px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  border: 2px solid;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
  white-space: nowrap;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn:active {
  transform: translateY(0);
}

.btn-primary {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
  box-shadow: 0 4px 14px -4px color-mix(in srgb, var(--primary-color) 50%, transparent);
}

.btn-secondary {
  background: transparent;
}

.btn-secondary--light {
  border-color: #e2e8f0;
  color: #475569;
}

.btn-secondary--dark {
  border-color: #334155;
  color: #cbd5e1;
}

.btn-secondary:hover {
  background: rgba(100, 116, 139, 0.1);
}

/* ── SUGGESTIONS ── */
.error-suggestions {
  text-align: left;
  max-width: 500px;
  margin: 0 auto;
}

.suggestions-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.suggestions-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggestions-list li {
  font-size: 0.875rem;
  padding-left: 24px;
  position: relative;
}

.suggestions-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  opacity: 0.5;
}

/* ── FLOATING SHAPES ── */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: var(--primary-color);
  opacity: 0.1;
  filter: blur(80px);
}

.shape-1 {
  width: 400px;
  height: 400px;
  top: -100px;
  right: -100px;
  animation: float1 20s ease-in-out infinite;
}

.shape-2 {
  width: 300px;
  height: 300px;
  bottom: -50px;
  left: -50px;
  animation: float2 15s ease-in-out infinite;
}

.shape-3 {
  width: 250px;
  height: 250px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: float3 18s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0); }
  33% { transform: translate(30px, -30px); }
  66% { transform: translate(-20px, 20px); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0); }
  33% { transform: translate(-40px, 30px); }
  66% { transform: translate(30px, -20px); }
}

@keyframes float3 {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.2); }
}

/* ── RESPONSIVE ── */
@media (max-width: 640px) {
  .error-title {
    font-size: 2rem;
  }

  .error-icon {
    font-size: 4rem;
  }

  .error-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>